import { Add, CloudUpload, Edit } from '@mui/icons-material';
import { Alert, Box, Checkbox, Divider, FormControl, FormControlLabel, Grid, IconButton, InputLabel, List, ListItem, ListItemText, MenuItem, Select, Stack, Switch, Tab, Tabs, TextField, Typography } from '@mui/material';
import { Button } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ImportTemplateEditor } from './importTemplateEditor.component';
import { makeStyles } from '@material-ui/core/styles';
import AdvancedSearch from 'modules/common/advancedSearch.component';
import ConfirmationDialog from 'modules/common/confirmationDialog.component';
import MaterialReactTable from 'material-react-table';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import Spinner from 'modules/common/spinner.component';

import {
  AUTH_IMPORT_INTAKE_FIELD_KEYS,
  AUTH_IMPORT_SECTIONS,
  authImportMappingTableProps,
  authImportTemplateDisplayColumns,
  createClientCustomField,
  getAdditionalFields,
  getAuthImportColumnMappings,
  getAuthImportFieldByFieldId,
  getAuthImportFieldLabel,
  getClientCustomFieldKey,
  getCustomFieldCommonIdFromKey,
  getInitialAuthImportMappings,
  getPositionError,
  getPositionValue,
  getVisibleSectionRows,
  isClientCustomFieldKey,
  isClientCustomFieldMapping,
} from './importAuthFiles.tableDefinitions';

const useAuthFileTemplateEditorStyles = makeStyles(theme => ({
  sectionCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #e0e0e0',
    borderRadius: 8,
  },
  mappingWorkspace: {
    minHeight: 420,
  },
  mappingSection: {
    padding: theme.spacing(1, 0, 2),
  },
  sectionHeader: {
    marginBottom: theme.spacing(1.5),
  },
}));

const AUTH_FILE_TEMPLATE = {
  templateId: 0,
  templateTypeId: 3,
  name: '',
  startingRow: 2,
  createIntakeRecords: false,
  clientRecordId: null,
  mappings: [],
};

const AUTH_FILE_MAPPING = {
  tempFileMappingId: 0,
  templateId: 0,
  fieldId: 0,
  position: null,
  customFieldCommonId: null,
};

const REQUIRED_TEMPLATE_NAME_ERROR = 'Template name is required';
const DATA_START_ROW_ERROR = 'Data start row is required and must be greater than zero';
const REQUIRED_MAPPING_ERROR = 'Position is required for fields included in the template';
const FORM_ERROR_MESSAGE = 'Please fix the highlighted errors before saving.';
const RELOCATION_COUNSELOR_SECTION_MESSAGE = 'Relocation Counselor values must be the email address of the relocation counselor\'s internal user account.';
const SELECT_CLIENT_SECTION_MESSAGE = 'Select a client to map its standard and custom fields.';
const LOADING_CUSTOM_FIELDS_SECTION_MESSAGE = 'Loading the client\'s custom fields...';
const CUSTOM_FIELDS_LOAD_ERROR_MESSAGE = 'Unable to load the client\'s custom fields.';
const UNAVAILABLE_CUSTOM_FIELD_LABEL = 'Unavailable custom field';
const UNAVAILABLE_CUSTOM_FIELDS_EDITOR_WARNING = 'Some custom field mappings are no longer available for this client. Remove or replace them before saving the template.';
const UNAVAILABLE_CUSTOM_FIELDS_SAVE_ERROR = 'Remove or replace the unavailable custom fields before saving.';
const UNAVAILABLE_CUSTOM_FIELDS_TEMPLATE_WARNING = 'Some custom field mappings are no longer available for this client. Edit the template to remove or replace them before running an import.';
const CUSTOM_FIELDS_TEMPLATE_LOAD_ERROR = 'Unable to load the custom fields for this template\'s client, so imports are unavailable. Reselect the template to try again.';

export const CLIENT_SEARCH_COLUMNS = [
  { name: 'clientRecordId', title: 'Id' },
  { name: 'clientCode', title: 'Client Code' },
  { name: 'shortName', title: 'Client Name' },
];

export const CLIENT_SEARCH_COLUMN_EXTENSIONS = [
  { columnName: 'clientRecordId', width: 150 },
  { columnName: 'clientCode', width: 200 },
  { columnName: 'shortName', width: 300 },
];

export const CLIENT_CUSTOM_FIELDS_STATUS = {
  ERROR: 'error',
  IDLE: 'idle',
  LOADED: 'loaded',
  LOADING: 'loading',
};

export const PENDING_AUTH_IMPORT_TEMPLATE_TABS = {
  DRY_RUN_RESULTS: 'dryRunResults',
  IMPORT_RESULTS: 'importResults',
  MAPPING_DEFINITIONS: 'mappingDefinitions',
};

const createAuthFileTemplate = () => ({
  ...AUTH_FILE_TEMPLATE,
  mappings: [],
});

const getPendingAuthImportTemplateMappings = template => template?.mappings ?? template?.pendingAuthMappings ?? [];

const getPositiveNumberError = (value, message) => {
  if (value === null || value === undefined || value === '') return message;

  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? null : message;
};

const getPositiveNumberValue = value => {
  if (value === '') return '';

  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : value;
};

const getTemplateFieldError = (field, value) => {
  switch (field) {
    case 'name':
      return value.trim() ? null : REQUIRED_TEMPLATE_NAME_ERROR;
    case 'startingRow':
      return getPositiveNumberError(value, DATA_START_ROW_ERROR);
    default:
      return null;
  }
};

const getTemplateErrors = template => ['name', 'startingRow'].reduce((errors, field) => {
  const error = getTemplateFieldError(field, template[field]);

  return error
    ? {
      ...errors,
      [field]: error,
    }
    : errors;
}, {});

const getVisibleAuthImportRows = (sections, selectedAdditionalFieldKeysBySection, mappingsByFieldKey, createIntakeRecords) => sections.reduce((visibleRows, section) => [
  ...visibleRows,
  ...getVisibleSectionRows(section, selectedAdditionalFieldKeysBySection[section.sectionKey] || [], mappingsByFieldKey, createIntakeRecords),
], []);

const getRequiredPositionErrors = visibleRows => visibleRows.reduce((errors, row) => (
  row.position === null || row.position === undefined || row.position === ''
    ? {
      ...errors,
      [row.fieldKey]: REQUIRED_MAPPING_ERROR,
    }
    : errors
), {});

const getPositionValidationErrors = mappingsByFieldKey => Object.values(mappingsByFieldKey).reduce((errors, mapping) => (
  mapping.positionError
    ? {
      ...errors,
      [mapping.fieldKey]: mapping.positionError,
    }
    : errors
), {});

const hasErrors = errors => Object.keys(errors).length > 0;

export const getPendingAuthImportTemplateId = template => template?.templateId || template?.id || template?.importTemplateId;

export const getPendingAuthImportTemplateName = template => template?.name ?? template?.templateName ?? '';

export const getPendingAuthImportTemplateList = response => {
  if (Array.isArray(response)) return response;

  return response?.templates ?? response?.pendingAuthTemplates ?? response?.data ?? [];
};

export const getPendingAuthImportResultErrors = importResults => importResults?.errors ?? importResults?.data?.errors ?? [];

const getPendingAuthImportResultAuthorizations = importResults => importResults?.pendingAuthorizations ?? importResults?.data?.pendingAuthorizations ?? [];

const getDisplayValue = value => {
  if (value === null || value === undefined || value === '') return '';

  return String(value);
};

const buildAuthFileTemplatePayload = (template, visibleRows) => ({
  ...template,
  mappings: visibleRows.map(row => ({
    ...AUTH_FILE_MAPPING,
    tempFileMappingId: row.tempFileMappingId ?? AUTH_FILE_MAPPING.tempFileMappingId,
    templateId: row.templateId ?? template.templateId,
    fieldId: row.fieldId,
    position: row.position,
    customFieldCommonId: row.customFieldCommonId ?? AUTH_FILE_MAPPING.customFieldCommonId,
  })),
});

const hydrateAuthFileTemplate = template => {
  const freshTemplate = createAuthFileTemplate();
  if (!template) return freshTemplate;

  return {
    ...freshTemplate,
    ...template,
    templateId: template.templateId ?? freshTemplate.templateId,
    templateTypeId: template.templateTypeId ?? freshTemplate.templateTypeId,
    name: getPendingAuthImportTemplateName(template),
    startingRow: template.startingRow ?? freshTemplate.startingRow,
    createIntakeRecords: template.createIntakeRecords ?? freshTemplate.createIntakeRecords,
    clientRecordId: template.clientRecordId ?? freshTemplate.clientRecordId,
    mappings: getPendingAuthImportTemplateMappings(template),
  };
};

const getMappingRowsByFieldKey = template => getPendingAuthImportTemplateMappings(template).reduce((rowsByFieldKey, mapping) => {
  // Client custom field labels and types come from the client's fields once they load, so only the mapping state is kept here
  if (isClientCustomFieldMapping(mapping)) {
    const fieldKey = getClientCustomFieldKey(mapping.customFieldCommonId);

    return {
      ...rowsByFieldKey,
      [fieldKey]: {
        ...mapping,
        fieldKey,
        positionError: null,
      },
    };
  }

  const field = getAuthImportFieldByFieldId(mapping.fieldId);
  if (!field) return rowsByFieldKey;

  return {
    ...rowsByFieldKey,
    [field.fieldKey]: {
      ...field,
      ...mapping,
      fieldId: field.fieldId,
      fieldKey: field.fieldKey,
      fieldName: field.fieldName,
      isSystemRequired: field.isSystemRequired,
      positionError: null,
    },
  };
}, {});

const hydrateAuthImportMappings = template => {
  const mappingsByFieldKey = getInitialAuthImportMappings();
  const fetchedRowsByFieldKey = getMappingRowsByFieldKey(template);

  return {
    ...mappingsByFieldKey,
    ...fetchedRowsByFieldKey,
  };
};

const getSelectedAdditionalFieldKeysBySection = template => {
  const fetchedRowsByFieldKey = getMappingRowsByFieldKey(template);

  return AUTH_IMPORT_SECTIONS.reduce((selectedBySection, section) => {
    const selectedFieldKeys = section.isClientCustomFieldSection
      ? Object.keys(fetchedRowsByFieldKey).filter(isClientCustomFieldKey)
      : section.fields.filter(field => (
        !field.isSystemRequired && fetchedRowsByFieldKey[field.fieldKey]
      )).map(field => field.fieldKey);

    return selectedFieldKeys.length > 0
      ? {
        ...selectedBySection,
        [section.sectionKey]: selectedFieldKeys,
      }
      : selectedBySection;
  }, {});
};

const getDisplayMappingRows = (template, clientCustomFields, clientCustomFieldsStatus, enumerations) => {
  const areCustomFieldsLoaded = clientCustomFieldsStatus === CLIENT_CUSTOM_FIELDS_STATUS.LOADED && hasLookupItems(enumerations, 'fieldStatuses');

  const mappingRows = getPendingAuthImportTemplateMappings(template).reduce((rows, mapping) => {
    if (isClientCustomFieldMapping(mapping)) {
      const customField = findClientCustomField(clientCustomFields, mapping.customFieldCommonId);
      const definition = areCustomFieldsLoaded
        ? getClientCustomFieldDefinition(mapping.customFieldCommonId, customField, enumerations, !isActiveClientCustomField(customField, enumerations))
        : createClientCustomField({ customFieldCommonId: mapping.customFieldCommonId, fieldName: 'Custom field', dataType: '', isUnavailable: false });

      return [
        ...rows,
        {
          ...definition,
          ...mapping,
          id: `${mapping.tempFileMappingId || definition.fieldKey}`,
        },
      ];
    }

    const field = getAuthImportFieldByFieldId(mapping.fieldId);
    if (!field) return rows;

    return [
      ...rows,
      {
        ...field,
        ...mapping,
        id: `${mapping.tempFileMappingId || mapping.fieldId}`,
        fieldId: field.fieldId,
        fieldName: field.fieldName,
        isSystemRequired: field.isSystemRequired,
        dataType: field.dataType,
      },
    ];
  }, []);

  return AUTH_IMPORT_SECTIONS.reduce((displayRows, section) => {
    const sectionRows = mappingRows.filter(row => row.sectionKey === section.sectionKey)
      .sort((firstRow, secondRow) => Number(firstRow.position || 0) - Number(secondRow.position || 0));

    if (sectionRows.length === 0) return displayRows;

    return [
      ...displayRows,
      {
        id: `section-${section.sectionKey}`,
        isSectionHeader: true,
        sectionKey: section.sectionKey,
        sectionName: section.sectionName,
      },
      ...sectionRows,
    ];
  }, []);
};

const isSameId = (firstValue, secondValue) => Boolean(firstValue && secondValue) && String(firstValue).toLowerCase() === String(secondValue).toLowerCase();

const getLookupIdValues = item => [
  item?.id,
  item?.Id,
  item?.value,
  item?.countryId,
  item?.countryID,
  item?.provinceId,
  item?.provinceID,
  item?.stateId,
  item?.guid,
];

export const getLookupCollection = (enumerations, collectionKey) => enumerations?.[collectionKey] ?? enumerations?.allEnums?.[collectionKey] ?? [];

const findLookupItem = (items, id) => items?.find(value => getLookupIdValues(value).some(lookupId => isSameId(lookupId, id)));

export const hasLookupItems = (enumerations, collectionKey) => getLookupCollection(enumerations, collectionKey).length > 0;

export const getLookupTitle = (items, id) => {
  if (!id) return '';

  const item = findLookupItem(items, id);
  return item?.title ?? item?.longName ?? item?.shortName ?? item?.description ?? item?.name ?? String(id);
};

export const findClientCustomField = (clientCustomFields, customFieldCommonId) => clientCustomFields?.find(customField => (
  isSameId(customField.customFieldCommonId, customFieldCommonId)
));

export const isActiveClientCustomField = (customField, enumerations) => Boolean(customField)
  && findLookupItem(getLookupCollection(enumerations, 'fieldStatuses'), customField.fieldStatusId)?.title?.trim().toLowerCase() === 'active';

export const getClientCustomFieldDefinition = (customFieldCommonId, customField, enumerations, isUnavailable) => createClientCustomField({
  customFieldCommonId,
  fieldName: customField?.fieldLabel || UNAVAILABLE_CUSTOM_FIELD_LABEL,
  dataType: customField ? getLookupTitle(getLookupCollection(enumerations, 'fieldTypes'), customField.fieldTypeId) : '',
  isUnavailable,
});

// Active fields (standard and custom) can be mapped. Mapped fields that are inactive or deleted stay listed and flagged
// so the user can remove them, but they drop out of the list once unselected and cannot be selected again.
export const getClientCustomFieldDefinitions = (clientCustomFields, enumerations, selectedFieldKeys) => {
  const activeDefinitions = clientCustomFields
    .filter(customField => isActiveClientCustomField(customField, enumerations))
    .map(customField => getClientCustomFieldDefinition(customField.customFieldCommonId, customField, enumerations, false))
    .sort((firstField, secondField) => firstField.fieldName.localeCompare(secondField.fieldName));
  const activeFieldKeys = new Set(activeDefinitions.map(field => field.fieldKey));

  const unavailableDefinitions = selectedFieldKeys
    .filter(fieldKey => !activeFieldKeys.has(fieldKey))
    .map(fieldKey => {
      const customFieldCommonId = getCustomFieldCommonIdFromKey(fieldKey);
      return getClientCustomFieldDefinition(customFieldCommonId, findClientCustomField(clientCustomFields, customFieldCommonId), enumerations, true);
    });

  return [...unavailableDefinitions, ...activeDefinitions];
};

export const getClientName = (clients, clientRecordId) => {
  if (!clientRecordId) return '';

  const client = clients?.find(value => isSameId(value.clientRecordId, clientRecordId) || isSameId(value.id, clientRecordId));
  return client?.shortName ?? client?.clientName ?? client?.description ?? client?.legalName ?? String(clientRecordId);
};

const getName = tempFile => {
  const middleInitial = tempFile.middleName?.trim() ? `${tempFile.middleName.trim().charAt(0)}.` : '';

  return [tempFile.firstName, middleInitial, tempFile.lastName].filter(Boolean).join(' ');
};

const getEmailAddress = tempFile => tempFile.workEmail || tempFile.personalEmail || tempFile.spouseEmail || '';

const getLocation = (city, provinceId, countryId, enumerations) => [
  city,
  getLookupTitle(getLookupCollection(enumerations, 'provinces'), provinceId),
  getLookupTitle(getLookupCollection(enumerations, 'countries'), countryId),
].filter(Boolean).join(', ');

const getTempFileStatus = (tempFileStatusId, enumerations) => getLookupTitle(getLookupCollection(enumerations, 'tempFileStatuses'), tempFileStatusId);

const getPendingAuthImportResultRows = (importResults, enumerations, clients) => getPendingAuthImportResultAuthorizations(importResults).map((pendingAuthorization, index) => {
  const tempFile = pendingAuthorization.tempFile ?? pendingAuthorization;

  return {
    id: `pending-auth-${index}`,
    client: getClientName(clients, tempFile.clientRecordId),
    departure: getLocation(tempFile.departureCity, tempFile.departureProvinceId, tempFile.departureCountryId, enumerations),
    destination: getLocation(tempFile.destinationCity, tempFile.destinationProvinceId, tempFile.destinationCountryId, enumerations),
    emailAddress: getEmailAddress(tempFile),
    fileNumber: pendingAuthorization.fileNumber ?? '',
    intakeRecordId: pendingAuthorization.intakeRecordId ?? null,
    name: getName(tempFile),
    relocationCounselor: pendingAuthorization.relocationCounselorName ?? '',
    tempFileStatus: getTempFileStatus(tempFile.tempFileStatusId, enumerations),
  };
});

const pendingAuthImportResultColumns = [
  { accessorKey: 'name', header: 'Name' },
  { accessorKey: 'emailAddress', header: 'Email Address' },
  { accessorKey: 'departure', header: 'Departure' },
  { accessorKey: 'destination', header: 'Destination' },
  { accessorKey: 'client', header: 'Client' },
];

const getPendingAuthImportResultColumns = (showFileStatus, showIntakeColumns) => [
  ...pendingAuthImportResultColumns,
  ...(showIntakeColumns ? [{ accessorKey: 'relocationCounselor', header: 'Relocation Counselor' }] : []),
  ...(showFileStatus ? [{ accessorKey: 'tempFileStatus', header: 'File Status' }] : []),
  ...(showFileStatus && showIntakeColumns ? [{ accessorKey: 'fileNumber', header: 'File Number' }] : []),
];

export const getImportErrorMessage = error => {
  if (typeof error === 'string') return error;

  return error?.message ?? error?.errorMessage ?? error?.detail ?? JSON.stringify(error);
};

const PendingAuthImportResults = props => {
  const { clients, emptyMessage, enumerations, errorTitle, importResults, showFileStatus, showIntakeColumns, successMessage } = props;

  if (!importResults) {
    return (
      <Box pt={2}>
        <Typography variant="body2" color="textSecondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  const errors = getPendingAuthImportResultErrors(importResults);
  if (errors.length > 0) {
    return (
      <Box pt={2}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorTitle} {errors.length} issue{errors.length === 1 ? '' : 's'}.
        </Alert>
        <List dense>
          {errors.map((error, index) => (
            <ListItem key={`${getImportErrorMessage(error)}-${index}`}>
              <Typography variant="body2">{getImportErrorMessage(error)}</Typography>
            </ListItem>
          ))}
        </List>
      </Box>
    );
  }

  const pendingAuthRows = getPendingAuthImportResultRows(importResults, enumerations, clients);

  return (
    <Box pt={2}>
      <Typography variant="body2" color="textSecondary" mb={2}>
        {successMessage}
      </Typography>
      <Stack direction="row" spacing={4} mb={2}>
        <Box>
          <Typography variant="body2" color="textSecondary">Total Pending Auths</Typography>
          <Typography variant="h6">{pendingAuthRows.length}</Typography>
        </Box>
        {showFileStatus && showIntakeColumns && (
          <Box>
            <Typography variant="body2" color="textSecondary">Intake Records Created</Typography>
            <Typography variant="h6">{pendingAuthRows.filter(row => row.intakeRecordId).length}</Typography>
          </Box>
        )}
      </Stack>
      <MaterialReactTable
        data={pendingAuthRows}
        columns={getPendingAuthImportResultColumns(showFileStatus, showIntakeColumns)}
        getRowId={row => row.id}
        enablePagination={false}
        enableBottomToolbar={false}
        enableTopToolbar={false}
        enableColumnActions={false}
        enableColumnFilters={false}
        enableGlobalFilter={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableHiding={false}
      />
    </Box>
  );
};

PendingAuthImportResults.propTypes = {
  clients: PropTypes.arrayOf(PropTypes.object),
  emptyMessage: PropTypes.string.isRequired,
  enumerations: PropTypes.object,
  errorTitle: PropTypes.string.isRequired,
  importResults: PropTypes.object,
  showFileStatus: PropTypes.bool,
  showIntakeColumns: PropTypes.bool,
  successMessage: PropTypes.string.isRequired,
};

PendingAuthImportResults.defaultProps = {
  clients: [],
  enumerations: null,
  importResults: null,
  showFileStatus: false,
  showIntakeColumns: false,
};

export const TabPanel = props => {
  const { children, currentTab, value } = props;

  if (currentTab !== value) return null;

  return <Box pt={2}>{children}</Box>;
};

TabPanel.propTypes = {
  children: PropTypes.node.isRequired,
  currentTab: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

export const AuthFileTemplateSelector = (props) => {
  const { isLoading, onTemplateSelected, selectedTemplateId, templates } = props;

  return (
    <div>
      <FormControl sx={{ minWidth: 350 }} size="small">
        <InputLabel id="auth-file-template-label">
          Select Pending Auth Import Template
        </InputLabel>
        <Select
          labelId="auth-file-template-label"
          id="auth-file-template-select"
          label="Select Pending Auth Import Template"
          disabled={isLoading}
          value={selectedTemplateId}
          onChange={event => onTemplateSelected(event.target.value)}
        >
          <MenuItem value="">Select a template</MenuItem>
          {templates.map(template => (
            <MenuItem key={getPendingAuthImportTemplateId(template)} value={String(getPendingAuthImportTemplateId(template))}>
              {getPendingAuthImportTemplateName(template)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};

AuthFileTemplateSelector.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  onTemplateSelected: PropTypes.func.isRequired,
  selectedTemplateId: PropTypes.string.isRequired,
  templates: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export const CreateImportTemplateButton = props => {
  const { disabled, isEditMode, onClick } = props;
  const Icon = isEditMode ? Edit : Add;

  return (
    <Button variant="contained" color="primary" startIcon={<Icon />} onClick={onClick} disabled={disabled}>
      {isEditMode ? 'Edit Template' : 'Create Pending Auth Template'}
    </Button>
  );
};

CreateImportTemplateButton.defaultProps = {
  disabled: false,
};

CreateImportTemplateButton.propTypes = {
  disabled: PropTypes.bool,
  isEditMode: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

const AuthImportSectionTable = props => {
  const {
    classes,
    createIntakeRecords,
    headerActions,
    isFieldSelectionDisabled,
    mappingsByFieldKey,
    onAdditionalFieldsChange,
    onPositionChange,
    section,
    sectionMessage,
    selectedAdditionalFieldKeys,
  } = props;
  const visibleRows = getVisibleSectionRows(section, selectedAdditionalFieldKeys, mappingsByFieldKey, createIntakeRecords);
  const hasVisibleRows = visibleRows.length > 0;
  const additionalFields = getAdditionalFields(section);
  const showFieldSelector = additionalFields.length > 0 || Boolean(headerActions);
  const columns = useMemo(() => getAuthImportColumnMappings({
    onPositionChange,
  }), [onPositionChange]);

  return (
    <Box className={classes.mappingSection}>
      <Stack className={classes.sectionHeader} direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography variant="h6" gutterBottom>
            {section.sectionName}
          </Typography>
          {sectionMessage && (
            <Typography variant="body2" color="textSecondary">
              {sectionMessage}
            </Typography>
          )}
        </Box>
        {showFieldSelector && (
          <Stack direction="row" spacing={2} alignItems="center">
            {headerActions}
            <FormControl size="small" sx={{ minWidth: 280 }} disabled={isFieldSelectionDisabled || additionalFields.length === 0}>
              <Select
                multiple
                displayEmpty
                value={selectedAdditionalFieldKeys}
                renderValue={selectedFieldKeys => (
                  selectedFieldKeys.length === 0
                    ? 'Add additional fields'
                    : `${selectedFieldKeys.length} additional field${selectedFieldKeys.length === 1 ? '' : 's'} selected`
                )}
                onChange={event => onAdditionalFieldsChange(section.sectionKey, event.target.value)}
              >
                {additionalFields.map(field => (
                  <MenuItem key={field.fieldKey} value={field.fieldKey}>
                    <Checkbox checked={selectedAdditionalFieldKeys.includes(field.fieldKey)} color="primary" size="small" />
                    <ListItemText
                      primary={getAuthImportFieldLabel(field)}
                      primaryTypographyProps={field.isUnavailable ? { color: 'error' } : undefined}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        )}
      </Stack>
      {hasVisibleRows && (
        <MaterialReactTable
          data={visibleRows}
          columns={columns}
          getRowId={row => row.fieldKey}
          {...authImportMappingTableProps}
        />
      )}
    </Box>
  );
};

AuthImportSectionTable.propTypes = {
  classes: PropTypes.object.isRequired,
  createIntakeRecords: PropTypes.bool.isRequired,
  headerActions: PropTypes.node,
  isFieldSelectionDisabled: PropTypes.bool,
  // Client custom field entries only hold mapping state; their labels come from the section's field definitions
  mappingsByFieldKey: PropTypes.objectOf(PropTypes.shape({
    dataType: PropTypes.string,
    fieldKey: PropTypes.string,
    fieldName: PropTypes.string,
    isSystemRequired: PropTypes.bool,
    position: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    positionError: PropTypes.string,
    sectionKey: PropTypes.string,
  })).isRequired,
  onAdditionalFieldsChange: PropTypes.func.isRequired,
  onPositionChange: PropTypes.func.isRequired,
  section: PropTypes.shape({
    fields: PropTypes.arrayOf(PropTypes.object).isRequired,
    sectionKey: PropTypes.string.isRequired,
    sectionName: PropTypes.string.isRequired,
  }).isRequired,
  sectionMessage: PropTypes.node.isRequired,
  selectedAdditionalFieldKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
};

AuthImportSectionTable.defaultProps = {
  headerActions: null,
  isFieldSelectionDisabled: false,
};

export const AuthFileTemplateEditor = props => {
  const { clients, enumerations, initialTemplate, isEditMode, onClose, onLoadClientCustomFields, onSaveTemplate, open, sectionMessages } = props;
  const classes = useAuthFileTemplateEditorStyles();
  const [template, setTemplate] = useState(createAuthFileTemplate);
  const [templateErrors, setTemplateErrors] = useState({});
  const [mappingsByFieldKey, setMappingsByFieldKey] = useState(getInitialAuthImportMappings);
  const [selectedAdditionalFieldKeysBySection, setSelectedAdditionalFieldKeysBySection] = useState({});
  const [clientCustomFields, setClientCustomFields] = useState([]);
  const [clientCustomFieldsStatus, setClientCustomFieldsStatus] = useState(CLIENT_CUSTOM_FIELDS_STATUS.IDLE);

  const templateClientRecordId = template.clientRecordId;
  const hasPositionErrors = Object.values(mappingsByFieldKey).some(mapping => Boolean(mapping.positionError));
  const selectedCustomFieldKeys = selectedAdditionalFieldKeysBySection.customFields;
  const areCustomFieldsReady = clientCustomFieldsStatus === CLIENT_CUSTOM_FIELDS_STATUS.LOADED && hasLookupItems(enumerations, 'fieldStatuses');
  // Without the client's fields the custom section cannot render its mappings, so saving would drop them
  const isCustomFieldSavePending = Boolean(selectedCustomFieldKeys?.length) && !areCustomFieldsReady;

  const editorSections = useMemo(() => {
    const customFieldDefinitions = areCustomFieldsReady
      ? getClientCustomFieldDefinitions(clientCustomFields, enumerations, selectedCustomFieldKeys || [])
      : [];

    return AUTH_IMPORT_SECTIONS.map(section => (
      section.isClientCustomFieldSection
        ? { ...section, fields: customFieldDefinitions }
        : section
    ));
  }, [areCustomFieldsReady, clientCustomFields, enumerations, selectedCustomFieldKeys]);

  const hasUnavailableCustomFields = editorSections.some(section => section.fields.some(field => field.isUnavailable));

  React.useEffect(() => {
    if (!open) return;

    setTemplate(isEditMode ? hydrateAuthFileTemplate(initialTemplate) : createAuthFileTemplate());
    setTemplateErrors({});
    setMappingsByFieldKey(isEditMode ? hydrateAuthImportMappings(initialTemplate) : getInitialAuthImportMappings());
    setSelectedAdditionalFieldKeysBySection(isEditMode ? getSelectedAdditionalFieldKeysBySection(initialTemplate) : {});
  }, [initialTemplate, isEditMode, open]);

  React.useEffect(() => {
    if (!open || !templateClientRecordId) {
      setClientCustomFields([]);
      setClientCustomFieldsStatus(CLIENT_CUSTOM_FIELDS_STATUS.IDLE);
      return undefined;
    }

    let isCurrentClient = true;
    setClientCustomFieldsStatus(CLIENT_CUSTOM_FIELDS_STATUS.LOADING);

    onLoadClientCustomFields(templateClientRecordId).then(customFields => {
      if (!isCurrentClient) return;

      setClientCustomFields(customFields ?? []);
      setClientCustomFieldsStatus(customFields ? CLIENT_CUSTOM_FIELDS_STATUS.LOADED : CLIENT_CUSTOM_FIELDS_STATUS.ERROR);
    });

    return () => {
      isCurrentClient = false;
    };
  }, [onLoadClientCustomFields, open, templateClientRecordId]);

  const updateTemplateValue = (field, value) => {
    setTemplate(currentTemplate => ({
      ...currentTemplate,
      [field]: value,
    }));

    setTemplateErrors(currentErrors => {
      if (!currentErrors[field]) return currentErrors;

      const error = getTemplateFieldError(field, value);
      const nextErrors = { ...currentErrors };

      if (error) {
        nextErrors[field] = error;
      } else {
        delete nextErrors[field];
      }

      return nextErrors;
    });
  };

  const handlePositionChange = (fieldKey, value) => {
    setMappingsByFieldKey(currentMappings => ({
      ...currentMappings,
      [fieldKey]: {
        ...currentMappings[fieldKey],
        fieldKey,
        position: getPositionValue(value),
        positionError: getPositionError(value),
      },
    }));
  };

  const handleCreateIntakeRecordsChange = createIntakeRecords => {
    updateTemplateValue('createIntakeRecords', createIntakeRecords);

    if (createIntakeRecords) return;

    // Hidden intake fields are not saved, so drop their positions (and any errors that would block saving)
    setMappingsByFieldKey(currentMappings => AUTH_IMPORT_INTAKE_FIELD_KEYS.reduce((nextMappings, fieldKey) => ({
      ...nextMappings,
      [fieldKey]: {
        ...nextMappings[fieldKey],
        position: null,
        positionError: null,
      },
    }), currentMappings));
  };

  const handleClientChange = selectedClientRecordId => {
    // The search clears with an empty array
    const clientRecordId = selectedClientRecordId && !Array.isArray(selectedClientRecordId) ? selectedClientRecordId : null;
    if (clientRecordId === templateClientRecordId) return;

    updateTemplateValue('clientRecordId', clientRecordId);

    // Custom field mappings belong to the previous client's fields
    setSelectedAdditionalFieldKeysBySection(currentSelections => ({
      ...currentSelections,
      customFields: [],
    }));
    setMappingsByFieldKey(currentMappings => Object.fromEntries(
      Object.entries(currentMappings).filter(([fieldKey]) => !isClientCustomFieldKey(fieldKey)),
    ));
  };

  const handleAdditionalFieldsChange = (sectionKey, nextSelectedFieldKeys) => {
    const selectedFieldKeys = Array.isArray(nextSelectedFieldKeys) ? nextSelectedFieldKeys : [];
    const removedFieldKeys = (selectedAdditionalFieldKeysBySection[sectionKey] || []).filter(fieldKey => !selectedFieldKeys.includes(fieldKey));

    setSelectedAdditionalFieldKeysBySection(currentSelections => ({
      ...currentSelections,
      [sectionKey]: selectedFieldKeys,
    }));

    if (removedFieldKeys.length === 0) return;

    setMappingsByFieldKey(currentMappings => removedFieldKeys.reduce((nextMappings, fieldKey) => ({
      ...nextMappings,
      [fieldKey]: {
        ...nextMappings[fieldKey],
        position: null,
        positionError: null,
      },
    }), currentMappings));
  };

  const resetEditor = () => {
    setTemplate(createAuthFileTemplate());
    setTemplateErrors({});
    setMappingsByFieldKey(getInitialAuthImportMappings());
    setSelectedAdditionalFieldKeysBySection({});
  };

  const handleClose = (...args) => {
    resetEditor();
    onClose(...args);
  };

  const handleSave = async () => {
    if (hasUnavailableCustomFields || isCustomFieldSavePending) return;

    const visibleRows = getVisibleAuthImportRows(editorSections, selectedAdditionalFieldKeysBySection, mappingsByFieldKey, template.createIntakeRecords);
    const nextTemplateErrors = getTemplateErrors(template);
    const nextPositionErrors = {
      ...getPositionValidationErrors(mappingsByFieldKey),
      ...getRequiredPositionErrors(visibleRows),
    };

    setTemplateErrors(nextTemplateErrors);
    setMappingsByFieldKey(currentMappings => Object.entries(nextPositionErrors).reduce((nextMappings, [fieldKey, error]) => ({
      ...nextMappings,
      [fieldKey]: {
        ...nextMappings[fieldKey],
        positionError: error,
      },
    }), currentMappings));

    if (hasErrors(nextTemplateErrors) || hasErrors(nextPositionErrors)) return;

    const payload = buildAuthFileTemplatePayload(template, visibleRows);
    const wasSaved = await onSaveTemplate(payload);

    if (wasSaved !== false) {
      handleClose();
    }
  };

  const getFormErrorMessage = () => {
    if (hasUnavailableCustomFields) return UNAVAILABLE_CUSTOM_FIELDS_SAVE_ERROR;
    if (hasErrors(templateErrors) || hasPositionErrors) return FORM_ERROR_MESSAGE;
    return '';
  };

  const getCustomFieldsSectionMessage = () => {
    if (!templateClientRecordId) return SELECT_CLIENT_SECTION_MESSAGE;
    if (clientCustomFieldsStatus === CLIENT_CUSTOM_FIELDS_STATUS.ERROR) return CUSTOM_FIELDS_LOAD_ERROR_MESSAGE;
    if (!areCustomFieldsReady) return LOADING_CUSTOM_FIELDS_SECTION_MESSAGE;
    return '';
  };

  const formErrorMessage = getFormErrorMessage();
  const editorSectionMessages = {
    ...sectionMessages,
    ...(template.createIntakeRecords && { authorizationInfo: RELOCATION_COUNSELOR_SECTION_MESSAGE }),
    customFields: getCustomFieldsSectionMessage(),
  };

  const clientSearch = (
    <Box sx={{ width: 280 }}>
      <AdvancedSearch
        labelText="Client"
        displayValueAccessorKey="shortName"
        saveIdValueAccessorKey="clientRecordId"
        searchingCriteria={CLIENT_SEARCH_COLUMNS}
        searchingCriteriaExtensions={CLIENT_SEARCH_COLUMN_EXTENSIONS}
        searchingRows={clients}
        value={getClientName(clients, templateClientRecordId)}
        id="pending-auth-import-template-client"
        setIdValue={handleClientChange}
        size="small"
        fullWidth
      />
    </Box>
  );

  return (
    <ImportTemplateEditor
      open={open}
      onClose={handleClose}
      title={isEditMode ? 'Edit Pending Auth Import Template' : 'Create Pending Auth Import Template'}
      secondaryText="Define the template details and field mappings for a pending authorization import"
      actions={(
        <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center">
          {formErrorMessage && (
            <Typography color="error" variant="body2">
              {formErrorMessage}
            </Typography>
          )}
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={hasPositionErrors || hasUnavailableCustomFields || isCustomFieldSavePending}
            onClick={handleSave}
          >
            {isEditMode ? 'Update Template' : 'Save Template'}
          </Button>
        </Stack>
      )}
    >
      <Box className={classes.sectionCard}>
        <Typography variant="h6" gutterBottom>
          Template Details
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Template Name"
              variant="outlined"
              size="small"
              required
              error={Boolean(templateErrors.name)}
              helperText={templateErrors.name || ''}
              value={template.name}
              onChange={event => updateTemplateValue('name', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Data Start Row"
              variant="outlined"
              size="small"
              required
              error={Boolean(templateErrors.startingRow)}
              helperText={templateErrors.startingRow || ''}
              value={template.startingRow}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              onChange={event => updateTemplateValue('startingRow', getPositiveNumberValue(event.target.value))}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              label="Create Intake Records"
              control={(
                <Switch
                  color="primary"
                  checked={Boolean(template.createIntakeRecords)}
                  onChange={event => handleCreateIntakeRecordsChange(event.target.checked)}
                />
              )}
            />
          </Grid>
        </Grid>
      </Box>

      <Box className={`${classes.sectionCard} ${classes.mappingWorkspace}`}>
        {hasUnavailableCustomFields && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {UNAVAILABLE_CUSTOM_FIELDS_EDITOR_WARNING}
          </Alert>
        )}
        {editorSections.map((section, index) => (
          <React.Fragment key={section.sectionKey}>
            {index > 0 && <Divider />}
            <AuthImportSectionTable
              classes={classes}
              createIntakeRecords={Boolean(template.createIntakeRecords)}
              headerActions={section.isClientCustomFieldSection ? clientSearch : null}
              isFieldSelectionDisabled={section.isClientCustomFieldSection && !areCustomFieldsReady}
              mappingsByFieldKey={mappingsByFieldKey}
              section={section}
              sectionMessage={editorSectionMessages[section.sectionKey] || ''}
              selectedAdditionalFieldKeys={selectedAdditionalFieldKeysBySection[section.sectionKey] || []}
              onAdditionalFieldsChange={handleAdditionalFieldsChange}
              onPositionChange={handlePositionChange}
            />
          </React.Fragment>
        ))}
      </Box>
    </ImportTemplateEditor>
  );
};

AuthFileTemplateEditor.propTypes = {
  clients: PropTypes.arrayOf(PropTypes.object),
  enumerations: PropTypes.object,
  initialTemplate: PropTypes.object,
  isEditMode: PropTypes.bool.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // Resolves to the client's custom fields, or null when they could not be loaded
  onLoadClientCustomFields: PropTypes.func.isRequired,
  onSaveTemplate: PropTypes.func.isRequired,
  sectionMessages: PropTypes.objectOf(PropTypes.node),
};

AuthFileTemplateEditor.defaultProps = {
  clients: [],
  enumerations: null,
  initialTemplate: null,
  sectionMessages: {},
};

export const SelectedPendingAuthImportTemplate = props => {
  const {
    activeTab,
    clientCustomFields,
    clientCustomFieldsStatus,
    clients,
    dryRunResults,
    enumerations,
    importResults,
    isDryRunLoading,
    isImportSubmitting,
    isLoading,
    onDryRun,
    onFileSelected,
    onSubmitImport,
    onTabChange,
    selectedTemplateFile,
    template,
  } = props;
  const classes = useAuthFileTemplateEditorStyles();

  if (isLoading) {
    return (
      <Box className={classes.sectionCard}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Spinner size="1x" />
          <Typography variant="body2">Loading pending auth import template...</Typography>
        </Stack>
      </Box>
    );
  }

  if (!template) return null;

  const mappingRows = getDisplayMappingRows(template, clientCustomFields, clientCustomFieldsStatus, enumerations);
  const createIntakeRecords = Boolean(template.createIntakeRecords);
  const hasCustomFieldMappings = getPendingAuthImportTemplateMappings(template).some(isClientCustomFieldMapping);
  const hasUnavailableCustomFields = mappingRows.some(row => row.isUnavailable);
  const hasCustomFieldLoadError = hasCustomFieldMappings && clientCustomFieldsStatus === CLIENT_CUSTOM_FIELDS_STATUS.ERROR;
  // Imports stay blocked until the mapped custom fields are confirmed to still be available
  const isImportBlocked = hasCustomFieldMappings && (hasUnavailableCustomFields || clientCustomFieldsStatus !== CLIENT_CUSTOM_FIELDS_STATUS.LOADED);
  const hasDryRunErrors = getPendingAuthImportResultErrors(dryRunResults).length > 0;
  const canRunDryRun = Boolean(selectedTemplateFile && !isDryRunLoading && !isImportSubmitting && !isImportBlocked);
  const canSubmitImport = Boolean(selectedTemplateFile && dryRunResults && !hasDryRunErrors && !isDryRunLoading && !isImportSubmitting && !isImportBlocked);

  return (
    <>
      <Box className={classes.sectionCard}>
        <Grid container rowSpacing={1} columnSpacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>
              Template Details
            </Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
              <UploadTemplateFile
                key={getPendingAuthImportTemplateId(template)}
                clearTitle="Clear Pending Auth File"
                uploadLabel="Upload Pending Auth File"
                onFileSelected={onFileSelected}
              />
              <Button
                variant="contained"
                color="primary"
                disabled={!canRunDryRun}
                onClick={onDryRun}
                startIcon={isDryRunLoading ? <Spinner size="1x" color="inherit" /> : null}
              >
                {isDryRunLoading ? 'Running...' : 'Dry Run'}
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={!canSubmitImport}
                onClick={onSubmitImport}
                startIcon={isImportSubmitting ? <Spinner size="1x" color="inherit" /> : null}
              >
                {isImportSubmitting ? 'Submitting...' : 'Submit'}
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="textSecondary">Template Name</Typography>
            <Typography variant="body1">{getPendingAuthImportTemplateName(template)}</Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="textSecondary">Client</Typography>
            <Typography variant="body1">{getClientName(clients, template.clientRecordId)}</Typography>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" color="textSecondary">Data Start Row</Typography>
            <Typography variant="body1">{getDisplayValue(template.startingRow)}</Typography>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" color="textSecondary">Create Intake Records</Typography>
            <Typography variant="body1">{createIntakeRecords ? 'Yes' : 'No'}</Typography>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" color="textSecondary">Selected File</Typography>
            <Typography variant="body1">{selectedTemplateFile?.name ?? ''}</Typography>
          </Grid>
        </Grid>
      </Box>
      <Box className={classes.sectionCard}>
        <Tabs value={activeTab} onChange={(event, nextTab) => onTabChange(nextTab)} indicatorColor="primary" textColor="primary">
          <Tab value={PENDING_AUTH_IMPORT_TEMPLATE_TABS.MAPPING_DEFINITIONS} label="Mapping Definitions" sx={{ px: 4 }} />
          <Tab value={PENDING_AUTH_IMPORT_TEMPLATE_TABS.DRY_RUN_RESULTS} label="Dry Run Results" disabled={!dryRunResults} sx={{ px: 4 }} />
          <Tab value={PENDING_AUTH_IMPORT_TEMPLATE_TABS.IMPORT_RESULTS} label="Import Results" disabled={!importResults} sx={{ px: 4 }} />
        </Tabs>
        <TabPanel currentTab={activeTab} value={PENDING_AUTH_IMPORT_TEMPLATE_TABS.MAPPING_DEFINITIONS}>
          {hasUnavailableCustomFields && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {UNAVAILABLE_CUSTOM_FIELDS_TEMPLATE_WARNING}
            </Alert>
          )}
          {hasCustomFieldLoadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {CUSTOM_FIELDS_TEMPLATE_LOAD_ERROR}
            </Alert>
          )}
          <Typography variant="h6" gutterBottom>
            Template Field Mappings
          </Typography>
          <MaterialReactTable
            data={mappingRows}
            columns={authImportTemplateDisplayColumns}
            getRowId={row => String(row.id || row.tempFileMappingId || row.fieldId)}
            muiTableBodyRowProps={({ row }) => ({
              sx: row.original.isSectionHeader
                ? {
                  backgroundColor: '#f5f5f5',
                  '& td': {
                    borderBottom: '1px solid #d8d8d8',
                    borderTop: '1px solid #d8d8d8',
                  },
                }
                : {},
            })}
            {...authImportMappingTableProps}
          />
        </TabPanel>
        <TabPanel currentTab={activeTab} value={PENDING_AUTH_IMPORT_TEMPLATE_TABS.DRY_RUN_RESULTS}>
          <PendingAuthImportResults
            clients={clients}
            emptyMessage="Upload a file and run a dry run to preview pending auth import results."
            enumerations={enumerations}
            errorTitle="Dry run found"
            importResults={dryRunResults}
            showIntakeColumns={createIntakeRecords}
            successMessage={createIntakeRecords
              ? 'These pending auths will be created and advanced to intake with their relocation counselor when the import is submitted.'
              : 'These pending auths will be created when the import is submitted.'}
          />
        </TabPanel>
        <TabPanel currentTab={activeTab} value={PENDING_AUTH_IMPORT_TEMPLATE_TABS.IMPORT_RESULTS}>
          <PendingAuthImportResults
            clients={clients}
            emptyMessage="Submit a successfully validated file to view import results."
            enumerations={enumerations}
            errorTitle="Import found"
            importResults={importResults}
            showFileStatus
            showIntakeColumns={createIntakeRecords}
            successMessage={createIntakeRecords
              ? 'These pending auths were created and advanced to intake by the import submission.'
              : 'These pending auths were returned by the import submission.'}
          />
        </TabPanel>
      </Box>
    </>
  );
};

SelectedPendingAuthImportTemplate.propTypes = {
  activeTab: PropTypes.string.isRequired,
  clientCustomFields: PropTypes.arrayOf(PropTypes.object),
  clientCustomFieldsStatus: PropTypes.oneOf(Object.values(CLIENT_CUSTOM_FIELDS_STATUS)),
  clients: PropTypes.arrayOf(PropTypes.object),
  dryRunResults: PropTypes.object,
  enumerations: PropTypes.object,
  importResults: PropTypes.object,
  isDryRunLoading: PropTypes.bool.isRequired,
  isImportSubmitting: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onDryRun: PropTypes.func.isRequired,
  onFileSelected: PropTypes.func.isRequired,
  onSubmitImport: PropTypes.func.isRequired,
  onTabChange: PropTypes.func.isRequired,
  selectedTemplateFile: PropTypes.object,
  template: PropTypes.object,
};

SelectedPendingAuthImportTemplate.defaultProps = {
  clientCustomFields: [],
  clientCustomFieldsStatus: CLIENT_CUSTOM_FIELDS_STATUS.IDLE,
  clients: [],
  dryRunResults: null,
  enumerations: null,
  importResults: null,
  selectedTemplateFile: null,
  template: null,
};


export const UploadTemplateFile = props => {
  const { clearTitle, disabled, onFileSelected, uploadLabel } = props;
  const [selectedFile, setSelectedFile] = useState(null);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const handleFileSelected = event => {
    const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    setSelectedFile(file);
    onFileSelected(file);

    event.target.value = '';
  };

  const handleFileCleared = () => {
    setSelectedFile(null);
    setShowConfirmationDialog(false);
    onFileSelected(null);
  };

  return (
    <Box>
      {selectedFile ? (
        <div>
          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton onClick={() => setShowConfirmationDialog(true)} color="error" size="small">
              <FontAwesomeIcon icon={['fas', 'trash']} />
            </IconButton>
            <Typography variant="body2">{selectedFile.name}</Typography>
          </Stack>
          <ConfirmationDialog
            open={showConfirmationDialog}
            title={clearTitle}
            confirmLabel="Yes"
            cancelLabel="No"
            onConfirm={() => handleFileCleared()}
            onCancel={() => setShowConfirmationDialog(false)}
          >
            Are you sure you want to clear the selected template file?
          </ConfirmationDialog>
        </div>
      ) : (
        <Button variant="contained" color="primary" startIcon={<CloudUpload />} component="label" disabled={disabled}>
          {uploadLabel}
          <input
            accept=".xlsx,.xls"
            hidden
            type="file"
            onChange={handleFileSelected}
          />
        </Button>
      )}
    </Box>
  );
};

UploadTemplateFile.propTypes = {
  clearTitle: PropTypes.string,
  disabled: PropTypes.bool,
  onFileSelected: PropTypes.func.isRequired,
  uploadLabel: PropTypes.string,
};

UploadTemplateFile.defaultProps = {
  clearTitle: 'Clear Template',
  disabled: false,
  uploadLabel: 'Upload Template File',
};

